import { Button, LoadingSpinner } from '@neo4j-ndl/react';
import { Center, SearchField } from '@nx/ui';
import { some } from 'lodash-es';
import { useEffect, useState } from 'react';

import { DeploymentConfigItem } from './deployment-config-item';

export interface Neo4jConfigWithServerInfo {
  serverId: string;
  serverName: string;
  neo4jConfig: Record<string, string | number | boolean>;
}

interface DeploymentConfigProps {
  isLoading: boolean;
  neo4jConfigs: Neo4jConfigWithServerInfo[];
}

interface Server {
  serverId: string;
  isOpen: boolean;
}

export const DeploymentConfig = ({ neo4jConfigs, isLoading }: DeploymentConfigProps) => {
  const [search, setSearch] = useState('');
  const [servers, setServers] = useState<Server[]>([]);

  const toggleServerIsOpen = (serverId?: string, isOpen?: boolean) => {
    if (serverId !== undefined) {
      setServers(
        servers.map((server) => {
          if (server.serverId === serverId) {
            const updatedServer: Server = {
              serverId: server.serverId,
              isOpen: !server.isOpen,
            };
            return updatedServer;
          }
          return server;
        }),
      );
    }
    if (isOpen !== undefined) {
      setServers(
        servers.map((server) => {
          const newState: Server = {
            serverId: server.serverId,
            isOpen: isOpen,
          };
          return newState;
        }),
      );
    }
  };

  // UseEffect should only run when the Neo4j Configurations change.
  // The config is refreshed when refetching deployments but if the size of the config does not change
  // then there is no need to refresh the state. Technically the servers could change even if the amount is the
  // same and then the state won't get refreshed but that is not a frequent enough event to warrant using
  // cpu cycles to verify that the servers are still the same.
  useEffect(() => {
    if (servers.length === neo4jConfigs.length) {
      return;
    }
    let isOpen = false;
    if (neo4jConfigs.length === 1) {
      isOpen = true;
    }
    const initialServers = neo4jConfigs.map((config) => {
      const server: Server = {
        serverId: config.serverId,
        isOpen,
      };
      return server;
    });
    setServers(initialServers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neo4jConfigs]);

  useEffect(() => {
    if (search !== '' && !some(servers, (server) => server.isOpen)) {
      toggleServerIsOpen(undefined, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="mb-6 flex flex-col gap-2">
      <div className="flex flex-row gap-2">
        <SearchField
          className="min-w-36 grow"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          htmlAttributes={{ 'aria-label': 'Search for DBMSs', 'data-testid': 'deployment-config-search' }}
        />
        <Button
          fill="outlined"
          color="neutral"
          onClick={() => toggleServerIsOpen(undefined, true)}
          htmlAttributes={{
            'data-testid': 'deployment-config-expand-button',
          }}
        >
          Expand all
        </Button>
        <Button
          fill="outlined"
          color="neutral"
          onClick={() => toggleServerIsOpen(undefined, false)}
          htmlAttributes={{
            'data-testid': 'deployment-config-collapse-button',
          }}
        >
          Collapse all
        </Button>
      </div>
      {isLoading && (
        <div className="mt-4">
          <Center>
            <LoadingSpinner className="m-auto" size="large" />
          </Center>
        </div>
      )}
      {neo4jConfigs.length > 0 && servers.length > 0 && (
        <div className="mt-2 flex flex-col gap-4">
          {neo4jConfigs.map((neo4jConfig) => {
            let isOpen = false;
            const foundServer = servers.find((server) => server.serverId === neo4jConfig.serverId);
            if (foundServer) {
              isOpen = foundServer.isOpen;
            }
            return (
              <DeploymentConfigItem
                key={neo4jConfig.serverId}
                serverId={neo4jConfig.serverId}
                serverName={neo4jConfig.serverName}
                neo4jConfig={neo4jConfig.neo4jConfig}
                filter={search}
                isOpen={isOpen}
                toggleIsOpen={toggleServerIsOpen}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
